// build-version: 20260519-2125 (fix handleSelectEvent to restore meetingId/passcode)
import React, { useState, useEffect, useRef } from "react";
import api from "../lib/api";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./EventCreate.css";
import DatePartsInput from "./DatePartsInput";
import AdminEventTabs from "./AdminEventTabs";

const PLATFORM_PREFIXES = ["Zoom: ", "Teams: ", "Google Meet: ", "Skype: ", "Slack: "];

/** 今日の日付を "YYYY-MM-DD" で返す。開催日・締切の下限に使う。 */
const todayYmd = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** "YYYY-MM-DD" の中で最も早い日付を返す（空文字は無視）。上限の重ね合わせに使う。 */
const earliest = (...dates) => dates.filter(Boolean).sort()[0] || undefined;

/** オンライン会議サービスごとの共有 URL ベース */
const PLATFORM_BASE_URLS = {
    "Zoom": "https://zoom.us/j/",
    "Teams": "https://teams.microsoft.com/l/meetup-join/",
    "Google Meet": "https://meet.google.com/",
    "Skype": "https://join.skype.com/",
    "Slack": "https://app.slack.com/client/",
};

const ALL_PLATFORM_BASE_URLS = Object.values(PLATFORM_BASE_URLS);

/** 指定 URL が「いずれかのプラットフォームのベース URL のみ／空」と一致するか */
const isPlatformBaseOnly = (url) => {
    if (!url || !url.trim()) return true;
    return ALL_PLATFORM_BASE_URLS.includes(url.trim());
};

const parseArray = (val) => {
    if (Array.isArray(val)) return val;
    if (!val) return [];
    try { return JSON.parse(val); } catch { return [val]; }
};

const formatDateForInput = (iso) => {
    if (!iso) return "";
    return iso.split("T")[0];
};

const formatTimeForInput = (iso) => {
    if (!iso) return "";
    const t = iso.includes("T") ? iso.split("T")[1] : iso;
    return t.substring(0, 5);
};

const EventCreate = () => {
    const navigate = useNavigate();
    const { eventId } = useParams();
    const isEditMode = !!eventId;
    const [allDeptData, setAllDeptData] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [tempSelectedDepts, setTempSelectedDepts] = useState([]);
    const [tempSelectedSecs, setTempSelectedSecs] = useState([]);
    const [filteredSections, setFilteredSections] = useState([]);
    const [targetGroup, setTargetGroup] = useState([]);
    const [selectedDept, setSelectedDept] = useState("");
    const [selectedSection, setSelectedSection] = useState("");
    const [selectedDepts, setSelectedDepts] = useState([]);
    const [selectedSections, setSelectedSections] = useState([]);
    const [sectionFilterDept, setSectionFilterDept] = useState("");
    const [eventDate, setEventDate] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const [baseEventName, setBaseEventName] = useState("");
    const [showAnketo, setShowAnketo] = useState(false);

    const [formData, setFormData] = useState({
        eventName: "", 
        description: "",
        eventDate: "",
        startTime: "",
        endTime: "",
        location: "",
        regDeadline: "",
        sendDeadline: "",
        targetGroup: [],
        rainPolicy: [],
        policy: [],
        hasAnketo: false,
        anketoQ1: "",
        anketoQ2: "",
        anketoQ3: "",
        anketoQ4: "",
    });
    const [locationType, setLocationType] = useState("other");
    const [onlinePlatform, setOnlinePlatform] = useState("Zoom");
    const [dateType, setDateType] = useState("single");
    const [currentUser, setCurrentUser] = useState({});
    const [message, setMessage] = useState("");

    const FIXED_ANKETO_QUESTIONS = [
        {
            question: "イベントの満足度を教えてください。",
            options: ["大変満足", "満足", "普通", "やや不満", "不満"],
        },
        {
            question: "改善点があれば教えてください。",
            options: ["特になし", "時間配分", "場所", "内容", "その他"],
        },
        {
            question: "今回のアンケートについて考えたことがありますか？",
            options: ["はい", "いいえ"],
        },
        {
            question: "内容や構成は分かりやすかったですか？",
            options: ["非常に分かりやすい", "分かりやすい", "普通", "分かりにくい"],
        },
        {
            question: "次回のイベントに期待することはありますか？",
            options: ["新製品情報", "交流会", "セミナー", "特になし"],
        },
    ];

    const [extraQuestions, setExtraQuestions] = useState(() => [
        ...FIXED_ANKETO_QUESTIONS,
        // 追加枠（任意）
        { question: "", options: [] },
    ]);


    const WEATHER_ICONS = {
        SUNNY: { id: "sunny", label: "晴れ", icon: "☀️", value: "晴れ" },
        CLOUDY: { id: "cloudy", label: "曇り", icon: "☁️", value: "曇り" },
        RAINY: { id: "rainy", label: "雨", icon: "☔", value: "雨" },
        SNOWY: { id: "snowy", label: "雪", icon: "❄️", value: "雪" },
    };
    // 開催時間の 時間 と 分 の 参照
    const endTimeRef = useRef(null);
    const endDateRef = useRef(null);
    const allStaffCheckRef = useRef(null);

    // Meeting ID と Passcode の オンライン会議
    const [meetingId, setMeetingId] = useState("");
    const [passcode, setPasscode] = useState("");

    // 確認画面表示フラグ
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const verifiedSignatureRef = useRef("");
    //const today = new Date().toISOString().split("T")[0];
    const today = new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"

    // すべて登録を確認
    const isFormValid = () => {
        const selectedDate = dateType === "range" ? startDate : eventDate;
        const hasBaseInfo = 
            baseEventName.trim() !== "" && 
            formData.description.trim() !== "" &&
            selectedDate !== "" &&
            formData.startTime !== "" &&
            formData.endTime !== "" &&
            formData.regDeadline !== "" &&
            formData.sendDeadline !== "" &&
            formData.location.trim() !== "";

        const hasTarget = formData.targetGroup.length > 0 || selectedDepts.length > 0 || selectedSections.length > 0;
        return hasBaseInfo && hasTarget;
    };
    // ユーザー情報 + 部署データを一括取得
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [userRes, deptRes] = await Promise.all([
                    api.get("/auth/me/"),
                    api.get("/departments/"),
                ]);
                setCurrentUser(userRes.data);

                const data = deptRes.data;
                setAllDeptData(data);
                /* 部（xx00）一覧：0000（取締役会等）以外。子課が無い部（人事部など）も含む */
                setDepartments(
                    data.filter(
                        (item) =>
                            item.departmentId &&
                            item.departmentId !== "0000" &&
                            !item.departmentId.startsWith("00") &&
                            item.departmentId.endsWith("00")
                    )
                );
            } catch (err) {
                console.error("初期データ取得エラー:", err);
            }
        };
        fetchInitialData();
    }, []);

    // 編集モード: 部署マスタ取得後に、対象部署・課を部署マスタの実体（ID付き）へ解決し直す。
    // イベント読込時は targetGroup に部署「名」しか無いため departmentId に名前を入れており、
    // そのままではタグの削除や「(部) 課」表記が効かない。
    useEffect(() => {
        if (!isEditMode || allDeptData.length === 0) return;
        const findByName = (name) => allDeptData.find(d => d.departmentName === name);
        setSelectedDepts(prev => prev.map(d => findByName(d.departmentName) || d));
        setSelectedSections(prev => prev.map(s => findByName(s.departmentName) || s));
    }, [isEditMode, allDeptData]);

    // 編集モード: 既存イベントデータをロード
    useEffect(() => {
        if (!isEditMode) return;
        const fetchEventData = async () => {
            try {
                const res = await api.get(`/events/${eventId}/`);
                const data = res.data;

                // 場所種別を判定
                const locText = data.location || "";
                const detectedPrefix = PLATFORM_PREFIXES.find(p => locText.startsWith(p));
                const locType = detectedPrefix ? "online" : "other";
                const cleanLocation = detectedPrefix ? locText.replace(detectedPrefix, "") : locText;
                if (detectedPrefix) {
                    setOnlinePlatform(detectedPrefix.replace(": ", ""));
                }
                setLocationType(locType);

                // 日付種別を判定（endDate は endTime から取得）
                const evDate = formatDateForInput(data.eventDate);
                const evEndDate = formatDateForInput(data.endTime || data.eventDate);
                if (evDate && evEndDate && evDate !== evEndDate) {
                    setDateType("range");
                    setStartDate(evDate);
                    setEndDate(evEndDate);
                } else {
                    setDateType("single");
                    setEventDate(evDate);
                }

                // targetGroup
                const fullTG = parseArray(data.targetGroup);
                const deptNames = fullTG.filter(t => t.startsWith("dept:")).map(t => t.replace("dept:", ""));
                const secNames = fullTG.filter(t => t.startsWith("sec:")).map(t => t.replace("sec:", ""));
                setSelectedDepts(deptNames.map(name => ({ departmentName: name, departmentId: name })));
                setSelectedSections(secNames.map(name => ({ departmentName: name, departmentId: name })));
                setTargetGroup(fullTG.filter(t => !t.startsWith("dept:") && !t.startsWith("sec:")));

                // アンケート質問（旧: string[] / 新: {question, options}[]）
                const qs = Array.isArray(data.anketo_questions) ? data.anketo_questions : [];
                const stripPrefix = (s) => String(s || "").replace(/^Q\d+\s*[:.：]\s*/i, "");
                const normalizedObjs = qs
                    .map((q) => {
                        if (typeof q === "string") {
                            return { question: stripPrefix(q), options: [] };
                        }
                        if (q && typeof q === "object") {
                            const question = stripPrefix(q.question ?? q.q ?? q.text ?? "");
                            const options = Array.isArray(q.options) ? q.options.filter(Boolean) : [];
                            return { question, options };
                        }
                        return null;
                    })
                    .filter(Boolean);
                setExtraQuestions(
                    normalizedObjs.length > 0
                        ? normalizedObjs
                        : [...FIXED_ANKETO_QUESTIONS, { question: "", options: [] }]
                );

                const rawTitle = data.eventName || "";
                const dateRegex = /_(\d{8})$/;
                const baseFromApi = dateRegex.test(rawTitle)
                    ? rawTitle.replace(dateRegex, "")
                    : rawTitle;
                setBaseEventName(baseFromApi);
                // オンライン会議の Meeting ID とパスコードを復元
                setMeetingId(data.meeting_id ?? data.meetingId ?? data.meetingID ?? "");
                setPasscode(data.passcode ?? data.passCode ?? data.password ?? "");
                // meeting_id / passcode があればオンラインとして扱う
                if ((data.meeting_id || data.passcode) && locType !== "online") {
                    setLocationType("online");
                }

                setFormData(prev => ({
                    ...prev,
                    eventName: rawTitle,
                    description: data.description || "",
                    startTime: formatTimeForInput(data.startTime),
                    endTime: formatTimeForInput(data.endTime),
                    location: cleanLocation,
                    regDeadline: formatDateForInput(data.regDeadline),
                    sendDeadline: formatDateForInput(data.sendDeadline),
                    targetGroup: fullTG,
                    rainPolicy: parseArray(data.rainPolicy),
                    policy: locType === "online" ? [] : parseArray(data.policy),
                    hasAnketo: data.hasAnketo ?? false,
                    anketoQ1: "",
                    anketoQ2: "",
                    anketoQ3: "",
                    anketoQ4: "",
                    weather: data.weather || "",
                }));
            } catch (err) {
                console.error("イベント読み込みエラー:", err);
                alert("イベントデータの読み込みに失敗しました。");
            }
        };
        fetchEventData();
    }, [eventId, isEditMode]);
    {/*}

    const handleDeptChange = (e) => {
        const selectedId = String(e.target.value); 
        setSelectedDept(selectedId);
        setSelectedSection(""); 

        if (!selectedId) {
            setFilteredSections([]);
            return;
        }

        const parentDept = allDeptData.find(item => item.departmentId === selectedId);

        if (parentDept && parentDept.children) {
            setFilteredSections(parentDept.children);
        } else {
            setFilteredSections([]);
        }
    };
    */}
      
    const handleDeptChange = (e) => {
        const deptId = e.target.value;
        setSelectedDept(deptId);
        setSelectedSection("");
        const dept = allDeptData.find((d) => d.departmentId === deptId);
        setFilteredSections(dept?.children || []);
    };

    const handledDeptCheckboxChange = (deptId) => {
        setSelectedDept(prev => {
            if (prev.includes(deptId)) {
                const dept = allDeptData.find(d => d.departmentId === deptId);
                const chikdIds = dept?.children?.map(c => c.departmentId) || [];
                setTempSelectedSecs(prevSecs => prevSecs.filter(id => !chikdIds.includes(id)));
                return prev.filter(id => id !== deptId);
            } else {
                return [...prev, deptId];
            }
        });
    };

    const EXCLUSIVE_TARGETS = ["全社員", "部署単位", "課単位"];

    const handleExclusiveTarget = (label) => {
        if (isVerified) setIsVerified(false);
        const isSelected = formData.targetGroup.includes(label);
        if (isSelected) {
            setFormData(prev => ({
                ...prev,
                targetGroup: prev.targetGroup.filter(t =>
                    t !== label && !t.startsWith("dept:") && !t.startsWith("sec:")
                )
            }));
            setTargetGroup(prev => prev.filter(t => t !== label));
            if (label === "部署単位" || label === "課単位") {
                setSelectedDepts([]); setSelectedDept("");
                setSelectedSections([]); setSelectedSection("");
                setTempSelectedDepts([]); setTempSelectedSecs([]);
            }
        } else {
            setFormData(prev => ({
                ...prev,
                targetGroup: [
                    ...prev.targetGroup.filter(t =>
                        !EXCLUSIVE_TARGETS.includes(t) && !t.startsWith("dept:") && !t.startsWith("sec:")
                    ),
                    label
                ]
            }));
            setTargetGroup(prev => [
                ...prev.filter(t => !EXCLUSIVE_TARGETS.includes(t)),
                label
            ]);
            setSelectedDepts([]);
            setSelectedSections([]);
            setSelectedDept("");
            setSelectedSection("");
            setSectionFilterDept("");
            setFilteredSections([]);
            setTempSelectedDepts([]);
            setTempSelectedSecs([]);
        }
    };

    const handleAddDept = () => {
        if (!selectedDept) return;
        const dept = departments.find(d => d.departmentId === selectedDept);
        if (!dept) return;
        if (selectedDepts.some(d => d.departmentId === selectedDept)) {
            setSelectedDept(""); return;
        }
        setSelectedDepts(prev => [...prev, dept]);
        setFormData(prev => ({
            ...prev,
            targetGroup: [...prev.targetGroup, `dept:${dept.departmentName}`]
        }));
        setSelectedDept("");
    };

    const toggleTempDept = (deptId) => {
        setTempSelectedDepts(prev =>
            prev.includes(deptId) ? prev.filter(id => id !== deptId) : [...prev, deptId]
        );
    };

    const handleAddCheckedDepts = () => {
        if (tempSelectedDepts.length === 0 && tempSelectedSecs.length === 0) return;

        // 追加する部署を収集
        const deptsToAdd = departments.filter(d => tempSelectedDepts.includes(d.departmentId));

        // 追加する課を収集（全部署の子要素から tempSelectedSecs に一致するもの）
        const secsToAdd = [];
        allDeptData.forEach(dept => {
            if (dept.children) {
                dept.children.forEach(sec => {
                    if (tempSelectedSecs.includes(sec.departmentId)) {
                        secsToAdd.push(sec);
                    }
                });
            }
        });

        setSelectedDepts(prev => {
            const existingIds = new Set(prev.map(d => d.departmentId));
            const mergedDepts = [...prev, ...deptsToAdd.filter(d => !existingIds.has(d.departmentId))];
            return mergedDepts;
        });

        setSelectedSections(prev => {
            const existingIds = new Set(prev.map(s => s.departmentId));
            const mergedSecs = [...prev, ...secsToAdd.filter(s => !existingIds.has(s.departmentId))];
            return mergedSecs;
        });

        setFormData(fd => {
            const baseTG = (fd.targetGroup || []).filter(t => !t.startsWith("dept:") && !t.startsWith("sec:"));

            // 既存 + 新規 部署
            const existingDeptNames = new Set(
                (fd.targetGroup || []).filter(t => t.startsWith("dept:")).map(t => t.replace("dept:", ""))
            );
            const newDeptEntries = deptsToAdd
                .filter(d => !existingDeptNames.has(d.departmentName))
                .map(d => `dept:${d.departmentName}`);
            const allDeptEntries = [
                ...(fd.targetGroup || []).filter(t => t.startsWith("dept:")),
                ...newDeptEntries,
            ];

            // 既存 + 新規 課
            const existingSecNames = new Set(
                (fd.targetGroup || []).filter(t => t.startsWith("sec:")).map(t => t.replace("sec:", ""))
            );
            const newSecEntries = secsToAdd
                .filter(s => !existingSecNames.has(s.departmentName))
                .map(s => `sec:${s.departmentName}`);
            const allSecEntries = [
                ...(fd.targetGroup || []).filter(t => t.startsWith("sec:")),
                ...newSecEntries,
            ];

            return { ...fd, targetGroup: [...baseTG, ...allDeptEntries, ...allSecEntries] };
        });

        setTempSelectedDepts([]);
        setTempSelectedSecs([]);
        if (isVerified) setIsVerified(false);
    };

    const handleRemoveDept = (deptId) => {
        const dept = selectedDepts.find(d => d.departmentId === deptId);
        setSelectedDepts(prev => prev.filter(d => d.departmentId !== deptId));
        if (dept) {
            setFormData(prev => ({
                ...prev,
                targetGroup: prev.targetGroup.filter(t => t !== `dept:${dept.departmentName}`)
            }));
        }
        if (isVerified) setIsVerified(false);
    };

    const handleAddSection = () => {
        if (!selectedSection) return;
        const sec = filteredSections.find(s => s.departmentId === selectedSection)
            || allDeptData.find(s => s.departmentId === selectedSection);
        if (!sec) return;
        if (selectedSections.some(s => s.departmentId === selectedSection)) {
            setSelectedSection(""); return;
        }
        setSelectedSections(prev => [...prev, sec]);
        setFormData(prev => ({
            ...prev,
            targetGroup: [...prev.targetGroup, `sec:${sec.departmentName}`]
        }));
        setSelectedSection("");
        if (isVerified) setIsVerified(false);
    };

    const handleRemoveSection = (secId) => {
        const sec = selectedSections.find(s => s.departmentId === secId);
        setSelectedSections(prev => prev.filter(s => s.departmentId !== secId));
        if (sec) {
            setFormData(prev => ({
                ...prev,
                targetGroup: prev.targetGroup.filter(t => t !== `sec:${sec.departmentName}`)
            }));
        }
        if (isVerified) setIsVerified(false);
    };

    const toggleTempSec = (secId) => {
        setTempSelectedSecs(prev =>
            prev.includes(secId) ? prev.filter(id => id !== secId) : [...prev, secId]
        );
    };

    {/*}
    const handleAddCheckedSecs = () => {
        if (tempSelectedSecs.length === 0) return;
        const toAdd = filteredSections.filter(s => tempSelectedSecs.includes(s.departmentId));
        if (toAdd.length === 0) return;

        setSelectedSections(prev => {
            const existingIds = new Set(prev.map(s => s.departmentId));
            const merged = [...prev, ...toAdd.filter(s => !existingIds.has(s.departmentId))];

            setFormData(fd => {
                const baseTG = (fd.targetGroup || []).filter(t => !t.startsWith("sec:"));
                const secEntries = merged.map(s => `sec:${s.departmentName}`);
                return { ...fd, targetGroup: [...baseTG, ...secEntries] };
            });

            return merged;
        });

        setTempSelectedSecs([]);
        if (isVerified) setIsVerified(false);
    };
    */}
    const handleAddCheckedSecs = () => {
        // 選択された課IDから、全体の部署データから該当する課を探して取得
        const newSelectedSections = [];
        allDeptData.forEach(dept => {
            if (dept.children) {
                dept.children.forEach(sec => {
                    if (tempSelectedSecs.includes(sec.departmentId)) {
                        newSelectedSections.push(sec);
                    }
                });
            }
        });

        setSelectedSections(prev => {
            const existingIds = new Set(prev.map(s => s.departmentId));
            // 既存の選択済み課と新たに選択された課をマージ（重複は排除）
            return [...prev, ...newSelectedSections.filter(s => !existingIds.has(s.departmentId))];
        });
    };
    //オンライン会議 - Zoom Meeting ID: 3桁 3-4桁 4桁 自動スペース
    const handleMeetingIdChange = (e) => {
        const value = e.target.value;
        if (onlinePlatform === "Zoom") {
            const digits = value.replace(/\D/g, '').slice(0, 11);
            let formatted = digits;
            if (digits.length > 7) {
                formatted = `${digits.slice(0, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;
            } else if (digits.length > 3) {
                formatted = `${digits.slice(0, 3)} ${digits.slice(3)}`;
            }
            setMeetingId(formatted);
        } else {
            setMeetingId(value);
        }
    };
    // ダプルクリックでアンケート表示切替
    const handleDoubleClick = () => {
        setShowAnketo(!showAnketo);    
    };

    const addQuestionArt = (index) => {
        const updated = [...extraQuestions];
        updated.splice(index + 1, 0, { question: "", options: [] });
        setExtraQuestions(updated);
    };
    const removeQuestionArt = (index) => {
        // Q1（イベント満足度）のみ削除不可
        if (index === 0) return;
        const updated = extraQuestions.filter((_, i) => i !== index);
        setExtraQuestions(updated.length > 0 ? updated : [...FIXED_ANKETO_QUESTIONS, { question: "", options: [] }]);
    };

    // イベント名の自動更新ロジック（新規作成のみ：開催日に応じて _YYYYMMDD を付与。修正画面では付けない）
    useEffect(() => {
        if (isEditMode) {
            setFormData((prev) => ({ ...prev, eventName: baseEventName }));
            return;
        }
        const dateString = dateType === "range" ? startDate : eventDate;
        let dateSuffix = "";
        if (dateString) {
            dateSuffix = dateString.replace(/-/g, "");
        }
        const finalEventName = dateSuffix ? `${baseEventName}_${dateSuffix}` : baseEventName;
        setFormData((prev) => ({
            ...prev,
            eventName: finalEventName,
        }));
    }, [baseEventName, eventDate, startDate, dateType, isEditMode]);


  // 入力変更 (イベント名以外)
  const handleChange = (e) => {
    const { name, value } = e.target;
    // eventName は useEffect で処理するため、ここではスキップ
    setFormData((prev) => {
      // 登録締切が変わったら、回答締切は「登録締切の1日前」までに収める
      if (name === "regDeadline") {
        const next = { ...prev, regDeadline: value };
        const maxSend = value ? addDays(value, -1) : "";
        if (next.sendDeadline && maxSend && next.sendDeadline > maxSend) {
          next.sendDeadline = maxSend;
        }
        return next;
      }
      return { ...prev, [name]: value };
    });
    if (isVerified) setIsVerified(false);
  };

  

    const handleBack = () => {
        scrollPageToTop();
        if (currentUser.is_staff === true || currentUser.is_superuser === true) {
            navigate("/admin/create_event");
        } else {
            navigate("/card_back");
        }
    };
    {/*}
    const handleTempSave = async () => {
        try {
            const draftData = {
                ...formData,
                //isDraft: true,
                status: "draft",
            };
            await api.post("/events/", draftData);
            alert("一時保存しました");

        } catch (err) {
            console.error(err);
            alert("一時保存失敗しました")
        }
    };
    */}
    const scrollPageToTop = () => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    };

    const handleCancel = () => {
        scrollPageToTop();
        const confirmCancel = window.confirm(
            "入力内容は保存されません。キャンセルしますか？"
        );
        if (confirmCancel) {
            if(currentUser.is_staff === true || currentUser.is_superuser === true) {
                navigate("/admin/create_event");
            } else {
                navigate("/card_back");
            }
            requestAnimationFrame(() => {
                scrollPageToTop();
            });
            setTimeout(scrollPageToTop, 0);
        }
    };

  // イベント名入力変更 (30文字制限付き)
  const handleBaseNameChange = (e) => {
    let value = e.target.value;
    if (value.length > 0 && value.trim().length === 0) {
        return;
    }
    if (value.length > 30) {
        value = value.substring(0, 30);
    }
    setBaseEventName(value);
    if (isVerified) setIsVerified(false);
  };

  const handleChangeNoOnlyspace = (e) => {
    const { name, value } = e.target;

    if (value.length > 0 && value.trim().length === 0) {
        return;
    }
    setFormData(prev => ({
        ...prev,
        [name]: value
    }));
    if (isVerified) setIsVerified(false);
  }
  

  const handleCheckbox = (e, field) => {
    const { value, checked } = e.target;
    
    setFormData(prevData => {
      const updated = checked
        ? [...prevData[field], value]
        : prevData[field].filter(item => item !== value);
      let newFormData = { ...prevData, [field]: updated };

      if (field === "rainPolicy" && value === "晴れ") {
        if (checked) {
          // 晴れにチェック → 開催を自動追加
          if (!newFormData.policy.includes("開催")) {
            newFormData.policy = [...newFormData.policy, "開催"];
          }
        } else {
          // 晴れのチェックを外す → 開催も外す
          newFormData.policy = newFormData.policy.filter(item => item !== "開催");
        }
      }
       
      return newFormData;
    });

    if (field === "targetGroup") {
      setTargetGroup(prev => {
        const updated = checked
          ? [...prev, value]
          : prev.filter(item => item !== value);
        return updated;
      });
    }

    if (isVerified) setIsVerified(false);
    
  }
  // form　で checkbox 天候 を処理  (targetGroup, rainPolicy)
  const handleRadioChange = (e, field) => {
    const { value } = e.target;
    let updatedRainPolicy = formData.rainPolicy;
    let updatedPolicy = formData.policy;

    if (field === "rainPolicy") {
        // (radio button)　1つだけ選ぶ
        updatedRainPolicy = [value]; 

        // 晴れ　を選んだら 開催を自動的に選ぶ
        if (value === "晴れ") {
            updatedPolicy = ["開催"];
        } else if (updatedPolicy.includes("開催") && updatedRainPolicy.length > 0) {
            // それ以外の天候を選んだら 開催を外す
             updatedPolicy = []; 
        }

    } else if (field === "policy") {
        //  (radio button)1つだけ選ぶ
        updatedPolicy = [value];
    }

    setFormData(prevData => ({ 
        ...prevData, 
        rainPolicy: updatedRainPolicy,
        policy: updatedPolicy,
    }));
    if (isVerified) setIsVerified(false);
};
  
  const handleSelectEvent = (eventData) => {
    console.log('選択リスト:', eventData);

    const currentLoc = eventData.location ? eventData.location.toLowerCase() : '';
    const newLocationType = (currentLoc === 'zoom' || currentLoc.startsWith('http')) ? 'online' : 'other';
    setLocationType(newLocationType);
    
    // handleSelectEvent で baseEventName を設定する
    const eventNameFromData = eventData.eventName || '';
    const dateRegex = /_(\d{8})$/;
    const match = eventNameFromData.match(dateRegex);
    let baseNameForInput = eventNameFromData;

    if (match) {
        // イベント名から末尾の _YYYYMMDD を取り除き、ベース名として設定
        baseNameForInput = eventNameFromData.replace(dateRegex, '');
    }
    setBaseEventName(baseNameForInput);

    // 開催時間: 24h 形式 (HH:MM) に変換するロジック (このロジックはすでに正しいです)
    const toHHMM = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    };

    setFormData({
      id: eventData.id,
      eventName: eventNameFromData, // 一旦フルネームを設定 (useEffectで上書きされる)
      description: eventData.description || '',
      eventDate: eventData.eventDate || '',
      startTime: toHHMM(eventData.startTime),
      endTime: toHHMM(eventData.endTime),
      targetGroup: Array.isArray(eventData.targetGroup) ? eventData.targetGroup : (eventData.targetGroup ? eventData.targetGroup.split(',').map(s => s.trim()) : []),
      location: eventData.location,
      regDeadline: eventData.regDeadline,
      sendDeadline: eventData.sendDeadline,
      rainPolicy: Array.isArray(eventData.rainPolicy) ? eventData.rainPolicy : (eventData.rainPolicy ? eventData.rainPolicy.split(',').map(s => s.trim()) : []),
      policy: Array.isArray(eventData.policy) ? eventData.policy : (eventData.policy ? eventData.policy.split(',').map(s => s.trim()) : []),
    });
    setDateType("single");
    setEventDate(eventData.eventDate || '');

    setMeetingId(
        eventData.meeting_id ?? eventData.meetingId ?? eventData.meetingID ?? ''
    );
    setPasscode(
        eventData.passcode ?? eventData.passCode ?? eventData.password ?? ''
    );

  };

  //  イベントform を処理(新規 または修正)
  const isOnlySpace = (value) =>{
        return value.trim().length === 0;
  };
  {/*}
  const nomalizeDate = (v) => {
    if (!v) return null;
    return v.replaceAll(/\//g, "-");
  };
    
  const nomalizeTime = (date, time) => {
    if (!date || !time) return null;
    return `${date}T${time}:00+09:00`;
  };

  const isValidDateFormat = (value) => {
    return /^\d{4}-\d{2}-\d{2}$/.test(value);
  };
  {/*}
  const handleSubmit = async (e) => {
    e.preventDefault();

    const selectedDate = dateType === "range" ? startDate : eventDate;
    if (selectedDate < today) {
        alert("開催日は今日以降の日付を選択してください。");
        return;
    }
    
    if (dateType === "range") {

        if (!startDate || !endDate) {
            alert("開始日と終了日を入力してください。")
            return;
        }
        if (endDate <= startDate) {
            alert("終了日は開始日より後の日付である必要があります");
            return;
        }
    }

    if (isOnlySpace(formData.eventName)) {
        alert("イベント名を入力してください（スペースの不可です）");
        return;
    }
    if (isOnlySpace(formData.description)) {
        alert("詳細を入力してください。(スペースの不可です)");
        return;
    }
    if (isOnlySpace(formData.location)) {
        alert("開催場所を入力してください。スペースの不可です");
        return;
    }
    

    const isConfirmed = window.confirm("イベントを作成・保存してもよろしいですか？");
    
    if (!isConfirmed) {
        return; 
    }

    // 日時のフォーマットを調整
    // YYYY-MM-DDTHH:MM:SSZ
    // 例: 2023-10-15T09:00:00Z
    // formData.startTime, formData.endTime は "HH:MM" フォーマット
    // formData.eventDate は "YYYY-MM-DD" フォーマット
    const rawStartDate = (dateType === "range" ? startDate : eventDate);
    const rawEndDate = (dateType === "range" ? endDate : eventDate);
    const baseStartDate = nomalizeDate(rawStartDate);
    const baseEndDate = nomalizeDate(rawEndDate);
    if (!isValidDateFormat(baseStartDate)){
        alert("開催日の形式が正しくありません(YYYY/MM/DD)");
        return;
    }
    if (selectedDate === "range" && !isValidDateFormat(baseEndDate)) {
        alert("終了日の形式が正しくありません(YYYY/MM/DD)");
        return;
    }
    //const startDateTime = `${baseStartDate}T${formData.startTime}:00+09:00`;
    //const endDateTime = `${baseEndDate}T${formData.endTime}:00+09:00`;
    const startDateTime = nomalizeTime(baseStartDate, formData.startTime);
    const endDateTime = nomalizeTime(baseEndDate, formData.endTime);
    if (!startDateTime || !endDateTime) {
        alert("開始時間・終了時間を入力してください。");
        return;
    }
    const nomalizeDeadline = nomalizeDate(formData.regDeadline);
    if (!isValidDateFormat(nomalizeDeadline)) {
        alert("締切の形式が正しくありません(YYYY/MM/DD)");
        return;
    }
	const anketoQuestions = formData.hasAnketo
    ? [
        "イベントの満足度を教えてください。",
        "改善点があれば教えてください。",
        "今回のアンケートについて考えたことがありますか？",
        "その他のコメントや提案があれば教えてください。",
        ...extraQuestions.filter(q => q.trim() !== "")
        ]
    : [];

    const start = new Date(startDateTime);
    const end = new Date(endDateTime);
    if (end <= start) {
        alert("終了時間は開始時間より後である必要があります");
        return;
    }
    
    // 送信するデータを準備
    const dataToSend = {
        //  eventName は formData から取得 (useEffectで自動計算された値)
        eventName: formData.eventName, 
        description: formData.description,
        eventDate: baseStartDate,
        location: formData.location,
        regDeadline: nomalizeDeadline,
        targetGroup: formData.targetGroup,
        rainPolicy: formData.rainPolicy,
        policy: formData.policy,
        // 送信するデータを準備
        startTime: startDateTime,
        endTime: endDateTime,
        hasAnketo: formData.hasAnketo,
        //anketo_questions: extraQuestions.filter(q => q.trim() !== ""),
        anketo_questions: anketoQuestions,
        status: "published",

    };


    // ② イベント作成データ送信
    

    try {
      await api.post("/events/", dataToSend);
      console.log("Send_data", dataToSend);
      //alert("イベントを作成しました！");
      const nextAction = window.confirm("イベントを作成しました！\n 続けてイベントを作成しますか");
      // reset logic
      if (nextAction) {
      setBaseEventName("");
      setFormData({
        eventName: "",
        description: "",
        eventDate: "",
        startTime: "",
        endTime: "",
        location: "",
        regDeadline: "",
        rainPolicy: [],
        policy: [],
        targetGroup: [],
        hasAnketo: false,
        anketoQuestion: "",
      });
    } else {
        navigate("/admin/create_event");
    }
      setConfirmPassword("");
      //setSelectedDept("");
      setMessage("✅ イベントが正常に登録されました。");
    } catch (err) {
      console.error(err);
      alert("作成に失敗しました。");
    }
  };
  */}
    const getFullData = (currentStatus = "draft") => {
        const selectedDate = dateType === "range" ? startDate : eventDate;
        const selectedEndDate = dateType === "range" ? endDate : eventDate;

        const startISO = (selectedDate && formData.startTime) 
            ? `${selectedDate}T${formData.startTime}:00` 
            : null;

        const endISO = (selectedEndDate && formData.endTime) 
            ? `${selectedEndDate}T${formData.endTime}:00` 
            : null;

        // selectedDepts / selectedSections を targetGroup に統合
        const baseTargetGroup = (formData.targetGroup || []).filter(
            t => !t.startsWith("dept:") && !t.startsWith("sec:")
        );
        const deptEntries = selectedDepts.map(d => `dept:${d.departmentName}`);
        const secEntries = selectedSections.map(s => `sec:${s.departmentName}`);
        const fullTargetGroup = [...baseTargetGroup, ...deptEntries, ...secEntries];

        let finalLocation = formData.location || "";
        if (locationType === "online") {
            finalLocation = `${onlinePlatform}: ${formData.location}`;
        }
        const qs = formData.hasAnketo
            ? extraQuestions
                .map((q) => {
                    if (!q) return null;
                    if (typeof q === "string") {
                        const text = String(q || "").trim();
                        return text ? { question: text, options: [] } : null;
                    }
                    const question = String(q.question || "").trim();
                    const options = Array.isArray(q.options) ? q.options.map((o) => String(o || "").trim()).filter(Boolean) : [];
                    return question ? { question, options } : null;
                })
                .filter(Boolean)
            : [];

        return {
            ...formData,
            location: finalLocation,
            eventDate: selectedDate || null,
            eventEndDate: selectedEndDate || null,
            regDeadline: formData.regDeadline || null,
            sendDeadline: formData.sendDeadline || null,
            startTime: startISO,
            endTime: endISO,
            // オンライン以外では会議情報を保存しない
            meeting_id: locationType === "online" ? (meetingId || "") : "",
            passcode: locationType === "online" ? (passcode || "") : "",
            status: currentStatus,
            targetGroup: fullTargetGroup,
            anketo_questions: qs,

            created_by: currentUser.id || currentUser.username || currentUser.memberId ,
            creator_name: currentUser.username || currentUser.id || currentUser.memberId,
        };
    };

    const getConfirmSignature = () => {
        try {
            return JSON.stringify(getFullData("published"));
        } catch {
            return "";
        }
    };

    useEffect(() => {
        if (!isVerified) return;
        const sig = getConfirmSignature();
        if (verifiedSignatureRef.current && sig !== verifiedSignatureRef.current) {
            setIsVerified(false);
        }
    }, [
        isVerified,
        formData,
        baseEventName,
        dateType,
        eventDate,
        startDate,
        endDate,
        locationType,
        onlinePlatform,
        selectedDepts,
        selectedSections,
        extraQuestions,
    ]);
    {/*}
    const handleOpenConfirm = () => {
        const selectedDate = dateType === "range" ? startDate : eventDate;
        if (!formData.eventName || !selectedDate) return alert("入力内容を確認してください");
        setShowConfirmModal(true);
    };
    */}
    const addDays = (dateStr, days) => {
        if (!dateStr) return "";
        const [y, m, d] = dateStr.split("-").map(Number);
        const dt = new Date(y, (m || 1) - 1, d || 1);
        dt.setDate(dt.getDate() + days);
        const yy = dt.getFullYear();
        const mm = String(dt.getMonth() + 1).padStart(2, "0");
        const dd = String(dt.getDate()).padStart(2, "0");
        return `${yy}-${mm}-${dd}`;
    };

    // 締切・回答締切の上限になる「開催日」。単日ならその日、期間なら開始日（開催が始まる日）。
    const eventDayForDeadline = (dateType === "single" ? eventDate : startDate) || undefined;

    const resolveParentDeptName = (sectionId) => {
        if (!sectionId) return "";
        const parent = allDeptData.find((d) => Array.isArray(d.children) && d.children.some((c) => c.departmentId === sectionId));
        return parent?.departmentName || "";
    };

    const sectionOptionLabel = (sec) => {
        const parentName = resolveParentDeptName(sec?.departmentId);
        const baseName = sec?.departmentName || "";
        if (parentName) return `(${parentName}) ${baseName}`;
        return `${baseName}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const dataToSend = getFullData("published");

        if (!dataToSend.eventDate || !dataToSend.startTime || !dataToSend.regDeadline) {
            alert("開催日、時間、締切日は必須です。");
            return;
        }

        // 新規作成時のみ: 開催日は今日以降、締切・回答締切は開催日まで（開催日を含む）。
        // 既存イベントの修正時は、当時の日付をそのまま残せるよう制限しない。
        if (!isEditMode) {
            const today = todayYmd();
            const eventDay = (dateType === "single" ? eventDate : startDate) || "";
            if (eventDay && eventDay < today) {
                alert("開催日は今日以降の日付を選択してください。");
                return;
            }
            if (eventDay && formData.regDeadline && formData.regDeadline > eventDay) {
                alert("締切は開催日までの日付を選択してください（開催日も指定できます）。");
                return;
            }
            if (eventDay && formData.sendDeadline && formData.sendDeadline > eventDay) {
                alert("回答締切は開催日までの日付を選択してください（開催日も指定できます）。");
                return;
            }
        }

        if (!dataToSend.location || !dataToSend.location.trim()) {
            alert("開催場所を入力してください。");
            return;
        }

        // 誤配信防止: 対象を空のまま保存すると全社員が対象になるため、ここで止める
        if (formData.targetGroup.includes("部署単位") && selectedDepts.length === 0 && selectedSections.length === 0) {
            alert("部署単位を選択した場合は、対象の部署または課を1つ以上追加してください。");
            return;
        }
        if (formData.targetGroup.includes("課単位") && selectedSections.length === 0) {
            alert("課単位を選択した場合は、対象の課を1つ以上追加してください。");
            return;
        }

        if (onlinePlatform === "Zoom" && !/^\d[\d ]*\d$/.test(meetingId.trim()) && meetingId.trim().length > 0) {
            alert("ZoomのMeeting ID は数字で入力してください。");
            return;
        }

        if (!window.confirm(isEditMode ? "イベントを更新しますか？" : "イベントを作成完了しますか？")) return;

        try {
            if (isEditMode) {
                await api.put(`/events/${eventId}/`, dataToSend);
                alert("イベントを更新しました！");
                navigate("/event/list");
            } else {
                await api.post("/events/", dataToSend);
                alert("イベントを作成完了しました！");
                navigate("/event/list");
            }
        } catch (err) {
            console.error("Submit Error:", err.response?.data);
            alert("保存に失敗しました。入力形式を確認してください。");
        }
    };

    const handleTempSave = async () => {
        const dataToSend = getFullData("draft");

        try {
            if (isEditMode) {
                await api.put(`/events/${eventId}/`, dataToSend);
                alert("一時保存しました（更新）！");
                navigate("/event/list");
            } else {
                await api.post("/events/", dataToSend);
                alert("一時保存しました！");
                navigate("/event/list");
            }
        } catch (err) {
            console.error("Temp Save Error:", err.response?.data);
            console.error("Temp Save Payload:", dataToSend);
            const detail = err.response?.data;
            let msg = "一時保存に失敗しました。";
            if (detail && typeof detail === "object") {
                const lines = Object.entries(detail).map(([k, v]) => {
                    const text = Array.isArray(v) ? v.join(", ") : (typeof v === "object" ? JSON.stringify(v) : String(v));
                    return `・${k}: ${text}`;
                });
                if (lines.length > 0) {
                    msg += "\n" + lines.join("\n");
                }
            } else if (typeof detail === "string") {
                msg += "\n" + detail;
            }
            alert(msg);
        }
    };

    const addQuestionField = () => {
        setExtraQuestions([...extraQuestions, { question: "", options: [] }]);
    };

    const handleExtraQuestionChange = (index, patch) => {
        setExtraQuestions((prev) => {
            const next = [...prev];
            const cur = next[index] && typeof next[index] === "object" ? next[index] : { question: "", options: [] };
            next[index] = { ...cur, ...patch };
            return next;
        });
    };

    /** 選択肢を1行ずつ編集（カンマ入力は使わない） */
    const getOptionRows = (options) => (Array.isArray(options) && options.length > 0 ? [...options] : [""]);

    const setOptionRowValue = (qIndex, optIndex, value) => {
        setExtraQuestions((prev) => {
            const next = [...prev];
            const cur = next[qIndex] && typeof next[qIndex] === "object" ? next[qIndex] : { question: "", options: [] };
            const rows = getOptionRows(cur.options);
            while (rows.length <= optIndex) rows.push("");
            rows[optIndex] = value;
            next[qIndex] = { ...cur, options: rows };
            return next;
        });
    };

    const addOptionRow = (qIndex) => {
        setExtraQuestions((prev) => {
            const next = [...prev];
            const cur = next[qIndex] && typeof next[qIndex] === "object" ? next[qIndex] : { question: "", options: [] };
            const rows = getOptionRows(cur.options);
            rows.push("");
            next[qIndex] = { ...cur, options: rows };
            return next;
        });
    };

    const removeOptionRow = (qIndex, optIndex) => {
        setExtraQuestions((prev) => {
            const next = [...prev];
            const cur = next[qIndex] && typeof next[qIndex] === "object" ? next[qIndex] : { question: "", options: [] };
            let rows = getOptionRows(cur.options);
            rows = rows.filter((_, i) => i !== optIndex);
            if (rows.length === 0) rows = [""];
            next[qIndex] = { ...cur, options: rows };
            return next;
        });
    };

    // 確認画面表示

  return (
        <div className="event-create-page-container">
            <AdminEventTabs active="create" currentUser={currentUser} />
            <h2 className="form-title">{isEditMode ? "イベント修正" : "イベント作成"}</h2>

            <form className="event-create-form" onSubmit={handleSubmit}>
                {/* 1. イベント名  */}
                <div className="form-row-group">
                    <label className="form-label-wide required" htmlFor="eventName">
                        イベント名 
                    </label>
                    <div className="form-field-wrapper">
                        <div className="event-name-input-group">
                            <input
                                type="text"
                                id="eventName"
                                name="eventName"
                                value={baseEventName}
                                onChange={handleBaseNameChange}
                                required
                                className="input-text-long"
                                placeholder="例: 全体会議_"
                            />
                            <span className="char-limit-text">(※30文字制限)</span>
                        </div>
                         {/*  最終的なイベント名のプレビュー */}
                         {baseEventName && (
                            <div className="event-name-preview" style={{ marginTop: '5px', fontSize: '0.9em', color: 'gray' }}>
                                → 最終イベント名: **{formData.eventName}**
                            </div>
                         )}
                    </div>
                </div>

                {/* 内容 */}
                <div className="form-row-group">
                    <label className="form-label-wide required" htmlFor="description">詳細</label>
                    <div className="event-discription-input">
                        <textarea
                            name="description"
                            value={formData.description}
                            /*onChange={handleChange}*/
                            onChange={handleChangeNoOnlyspace}
                            required
                            className="input-text-long"
                            placeholder="内容を入力してください。"
                        />
                    </div>
                </div>    
                {/* 2. 開催日  */}
                <div className="form-row-group">
                    <label className="form-label-wide required">開催日</label>
                    <div className="form-field-wrapper">
                        <div className="date-type-radios">
                            <div className="form-check form-check-inline">
                                <input
                                    type="radio" id="single" name="dateType" value="single"
                                    checked={dateType === "single"}
                                    onChange={(e) => setDateType(e.target.value)}
                                />
                                <label htmlFor="single">単日</label>
                                {/* Input  */}
                                {dateType === "single" && (
                                    <DatePartsInput
                                        name="eventDate"
                                        value={eventDate}
                                        onChange={(v) => setEventDate(v)}
                                        min={isEditMode ? undefined : todayYmd()}
                                        required
                                    />
                                )}
                            </div>

                            <div className="form-check form-check-inline">
                                <input
                                    type="radio" id="range" name="dateType" value="range"
                                    checked={dateType === "range"}
                                    onChange={(e) => setDateType(e.target.value)}
                                />
                                <label htmlFor="range">期間</label>
                                {/* Input  */}
                                {dateType === "range" && (
                                    <div className="date-range-inputs">
                                        <DatePartsInput
                                            name="startDate"
                                            value={startDate}
                                            onChange={(v) => setStartDate(v)}
                                            onComplete={() => endDateRef.current?.focus()}
                                            min={isEditMode ? undefined : todayYmd()}
                                            required
                                        />
                                        <span className="date-separator">～</span>
                                        <DatePartsInput
                                            ref={endDateRef}
                                            name="endDate"
                                            value={endDate}
                                            onChange={(v) => setEndDate(v)}
                                            onBlur={(e) => {
                                                const value = e.target.value;
                                                if (value && startDate && value <= startDate) {
                                                    alert("終了日は開始日より後の日付を選択してください。");
                                                }
                                            }}
                                            min={
                                                isEditMode
                                                    ? undefined
                                                    : (startDate ? addDays(startDate, 1) : todayYmd())
                                            }
                                            required
                                        />
                                    </div>
                                )}
                            </div>
                        </div>   
                    </div>
                </div>

                {/* 3. 開催時間  */}
                <div className="form-row-group">
                    <label className="form-label-wide required">開催時間</label>
                    <div className="form-field-wrapper time-inputs-wrapper">
                        
                        <input
                            type="time" name="startTime" value={formData.startTime}
                            required className="input-time"
                            onChange={(e) => {
                                const v = e.target.value;
                                setFormData(prev => ({ ...prev, startTime: v }));
                                if (v && v.length === 5) {
                                    endTimeRef.current?.focus();
                                }
                            }}
                            // type="time" はブラウザ標準で 24時間表示 (HH:MM) に対応しています。
                        />
                        <span className="time-separator">〜</span>
                        <input
                            ref={endTimeRef}
                            type="time" name="endTime" value={formData.endTime}
                            /*onChange={handleChange}*/
                            required className="input-time"
                            onBlur={(e) => {
                                const value = e.target.value;
                                if (value && formData.startTime && value <= formData.startTime) {
                                    alert("終了時間は開始時間より後の時刻を入力してください。");
                                }
                            }}
                            onChange={(e) => {
                                const value = e.target.value;
                                setFormData(prev => ({ ...prev, endTime: value }));
                                // 終了時間の時間と分が入力されたら全社員のチェックボックスにフォーカス
                                if (value && value.length === 5 && (!formData.startTime || value > formData.startTime)) {
                                    // 終了時間入力完了 → 対象者は全社員を自動選択
                                    setFormData(prev => {
                                        const baseTG = (prev.targetGroup || []).filter(
                                            t => !EXCLUSIVE_TARGETS.includes(t) && !t.startsWith("dept:") && !t.startsWith("sec:")
                                        );
                                        return { ...prev, targetGroup: [...baseTG, "全社員"] };
                                    });
                                    setTargetGroup(prev => [...prev.filter(t => !EXCLUSIVE_TARGETS.includes(t)), "全社員"]);
                                    setSelectedDepts([]);
                                    setSelectedSections([]);
                                    setSelectedDept("");
                                    setSelectedSection("");
                                    setSectionFilterDept("");
                                    setFilteredSections([]);
                                    setTempSelectedDepts([]);
                                    if (isVerified) setIsVerified(false);
                                }
                            }}
                            // type="time" はブラウザ標準で 24時間表示 (HH:MM) に対応しています。
                        />
                        
                    </div>
                </div>

                {/* 4. 参加対象者 */}
                <div className="form-row-group target-group-row">
                    <label className="form-label-wide required">参加対象者</label>
                    <div className="form-field-wrapper">
                        {/* 行1: 全社員 + 部署単位（同一行） */}
                        <div className="target-exclusive-row">
                            <div className="form-check form-check-inline">
                                <input
                                    ref={allStaffCheckRef}
                                    type="checkbox"
                                    id="excl_全社員"
                                    name="targetGroup"
                                    value="全社員"
                                    checked={formData.targetGroup.includes("全社員")}
                                    onChange={() => handleExclusiveTarget("全社員")}
                                />
                                <label htmlFor="excl_全社員">全社員</label>
                            </div>
                            <div className="form-check form-check-inline">
                                <input
                                    type="checkbox"
                                    id="excl_部署単位"
                                    value="部署単位"
                                    checked={formData.targetGroup.includes("部署単位")}
                                    onChange={() => handleExclusiveTarget("部署単位")}
                                />
                                <label htmlFor="excl_部署単位">部署単位</label>
                            </div>
                            <div className="form-check form-check-inline">
                                <input
                                    type="checkbox"
                                    id="excl_課単位"
                                    value="課単位"
                                    checked={formData.targetGroup.includes("課単位")}
                                    onChange={() => handleExclusiveTarget("課単位")}
                                />
                                <label htmlFor="excl_課単位">課単位</label>
                            </div>
                        </div>

                        {/* 部署単位チェック時: 部署→課 インライン選択 */}
                        {formData.targetGroup.includes("部署単位") && (
                            <div className="dept-inline-selector">
                                <div className="dept-inline-hint">部署を選択すると右側に課が表示されます</div>
                                {departments.map((dept) => {
                                    const isDeptChecked = tempSelectedDepts.includes(dept.departmentId);
                                    const sections = allDeptData.find(d => d.departmentId === dept.departmentId)?.children || [];
                                    return (
                                        <div key={dept.departmentId} className="dept-inline-row">
                                            <label className="dept-inline-label">
                                                <input
                                                    type="checkbox"
                                                    checked={isDeptChecked}
                                                    onChange={() => toggleTempDept(dept.departmentId)}
                                                />
                                                <span>{dept.departmentName}</span>
                                            </label>
                                            {isDeptChecked && sections.map(sec => (
                                                <label key={sec.departmentId} className="sec-inline-label">
                                                    <input
                                                        type="checkbox"
                                                        checked={tempSelectedSecs.includes(sec.departmentId)}
                                                        onChange={() => toggleTempSec(sec.departmentId)}
                                                    />
                                                    <span>{sec.departmentName}</span>
                                                </label>
                                            ))}
                                        </div>
                                    );
                                })}
                                <button type="button" className="btn-add-tag" onClick={handleAddCheckedDepts}
                                    style={{ marginTop: '8px' }}>
                                    追加
                                </button>
                            </div>
                        )}

                        {/* 課単位チェック時: 部を経由せず課を直接選択 */}
                        {formData.targetGroup.includes("課単位") && (
                            <div className="dept-select-group">
                                <div className="dept-inline-hint">対象の課を選んで「追加」してください</div>
                                <div className="dept-add-row">
                                    <select
                                        className="select-dropdown"
                                        value={selectedSection}
                                        onChange={(e) => setSelectedSection(e.target.value)}
                                    >
                                        <option value="">-- 課を選択 --</option>
                                        {allDeptData
                                            .filter(d => d.departmentId && !d.departmentId.endsWith("00"))
                                            .map((sec) => (
                                                <option key={sec.departmentId} value={sec.departmentId}>
                                                    {sectionOptionLabel(sec)}
                                                </option>
                                            ))}
                                    </select>
                                    <button type="button" className="btn-add-tag" onClick={handleAddSection}>追加</button>
                                </div>
                            </div>
                        )}

                        {/* 選択済み部署・課タグ */}
                        {(selectedDepts.length > 0 || selectedSections.length > 0) && (
                            <div className="selected-tags">
                                {selectedDepts.map((dept) => (
                                    <span key={dept.departmentId} className="dept-tag">
                                        {dept.departmentName}
                                        <button type="button" className="tag-remove-btn" onClick={() => handleRemoveDept(dept.departmentId)}>×</button>
                                    </span>
                                ))}
                                {selectedSections.map((sec) => (
                                    <span key={sec.departmentId} className="dept-tag" style={{ backgroundColor: '#d1ecf1' }}>
                                        {sectionOptionLabel(sec)}
                                        <button type="button" className="tag-remove-btn" onClick={() => handleRemoveSection(sec.departmentId)}>×</button>
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* 行2: 家族参加可能 */}
                        <div className="target-option-row">
                            <div className="form-check form-check-inline">
                                <input
                                    type="checkbox" id="familytg" value="家族参加可能"
                                    checked={formData.targetGroup.includes("家族参加可能")}
                                    onChange={(e) => handleCheckbox(e, "targetGroup")}
                                />
                                <label htmlFor="familytg">家族参加可能</label>
                            </div>
                        </div>
                        {/* 行3: パート・アルバイト・契約社員 */}
                        <div className="target-option-row">
                            <div className="form-check form-check-inline">
                                <input
                                    type="checkbox" id="otherstg" value="パート・アルバイト・契約社員"
                                    checked={formData.targetGroup.includes("パート・アルバイト・契約社員")}
                                    onChange={(e) => handleCheckbox(e, "targetGroup")}
                                />
                                <label htmlFor="otherstg">パート・アルバイト・契約社員</label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 5. 開催場所  */}
                <div className="form-row-group">
                    <label className="form-label-wide required">開催場所</label>
                    <div className="form-field-wrapper">
                        <div className="location-radios">
                            <div className="form-check form-check-inline">
                                <input
                                    type="radio" id="loc_online" value="online" name="locationType"
                                    checked={locationType === 'online'}
                                    onChange={() => {
                                        setLocationType('online');
                                        // オンライン選択時、選択中サービスの共有URLベースを自動入力
                                        const base = PLATFORM_BASE_URLS[onlinePlatform] || '';
                                        setFormData(prevData => ({ ...prevData, location: base, rainPolicy: [], policy: [] }));
                                    }}
                                />
                                <label htmlFor='loc_online'>オンライン会議</label>
                            </div>
                            <div className="form-check form-check-inline">
                                <input
                                    type="radio" id="loc_other" value="other" name="locationType"
                                    checked={locationType === 'other'}
                                    onChange={() => {
                                        setLocationType('other');
                                        // その他選択時、オンライン会議の入力内容をクリア
                                        setFormData(prevData => ({ ...prevData, location: "" }));
                                        setMeetingId("");
                                        setPasscode("");
                                    }}
                                />
                                <label htmlFor='loc_other'>その他</label>
                            </div>
                        </div>

                        {/* Input Location  */}
                        {locationType === 'other' && (
                            <input
                                type="text" name="location" value={formData.location}
                                /*onChange={handleChange}*/
                                onChange={handleChangeNoOnlyspace} className="input-text-long"
                                placeholder="場所を入力してください" required
                            />
                        )}
                        {locationType === 'online' && (
                            <div className="online-meeting-group">
                                <select
                                    className="select-dropdown"
                                    value={onlinePlatform}
                                    onChange={(e) => {
                                        const next = e.target.value;
                                        setOnlinePlatform(next);
                                        // プラットフォーム切替時、未入力 or 他サービスのベースURLのみの場合は自動で差し替え
                                        const nextBase = PLATFORM_BASE_URLS[next] || "";
                                        setFormData(prev => {
                                            const cur = (prev.location || "").trim();
                                            if (!cur || isPlatformBaseOnly(cur)) {
                                                return { ...prev, location: nextBase };
                                            }
                                            // 既に他サービスのベースで始まる場合は新サービスのベースに置換
                                            for (const baseUrl of ALL_PLATFORM_BASE_URLS) {
                                                if (cur.startsWith(baseUrl) && baseUrl !== nextBase) {
                                                    return { ...prev, location: nextBase + cur.slice(baseUrl.length) };
                                                }
                                            }
                                            return prev;
                                        });
                                    }}
                                >
                                    {["Zoom", "Teams", "Google Meet", "Skype", "Slack"].map(p => (
                                        <option key={p} value={p}>{p}</option>
                                    ))}
                                </select>
                                <input
                                    type="url"
                                    placeholder={`${onlinePlatform} の会議URLを入力してください（例: ${PLATFORM_BASE_URLS[onlinePlatform] || ""}）`}
                                    value={formData.location}
                                    onFocus={() => {
                                        // 空欄でフォーカスされた場合、ベースURLを初期表示
                                        setFormData(prev => {
                                            if (!prev.location) {
                                                return { ...prev, location: PLATFORM_BASE_URLS[onlinePlatform] || "" };
                                            }
                                            return prev;
                                        });
                                    }}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (/\s/.test(value)) return;
                                        setFormData(prev => ({ ...prev, location: value }));
                                    }}
                                    className="input-text-long"
                                    required
                                />
                                <small className="form-hint">
                                    共有URL（例: <code>{PLATFORM_BASE_URLS[onlinePlatform]}</code>）の後に会議IDを続けて入力してください。
                                </small>
                                <input 
                                    type="text"
                                    placeholder="ミーティングIDを入力してください"
                                    value={meetingId}
                                    onChange={(e) => setMeetingId(e.target.value)}
                                    className="input-text-long"
                                    required
                                />
                                <input
                                    type="text"
                                    placeholder="パスコードを入力してください"
                                    value={passcode}
                                    onChange={(e) => setPasscode(e.target.value)}
                                    className="input-text-long"
                                    required
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* 6. 開催条件 */}
                {locationType !== 'online' && (
                    <div className="form-row-group">
                        <label className="form-label-wide required">開催条件</label>
                        
                        
                        
                        <div className="target-checkboxes"> 
                            {/*}
                            <div className="weather-policy-container-horizontal">
                                {Object.keys(WEATHER_ICONS).map((key) => {
                                    const item = WEATHER_ICONS[key];
                                    return (
                                        
                                        <div className="form-check form-check-inline" key={item.value}> 
                                            <input
                                                type="radio" id={`weather-${item.value}`} value={item.value}
                                                name="rainPolicy" className="form-check-input"
                                                checked={formData.rainPolicy.includes(item.value)} 
                                                onChange={(e) => handleRadioChange(e, "rainPolicy")}
                                            />
                                            <label htmlFor={`weather-${item.value}`}>
                                                {item.icon} {item.label}
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>
                            */}
                            <div className="target-checkboxes" style={{ marginBottom: '15px' }}>
                                {Object.values(WEATHER_ICONS).map((item) => (
                                    <div className="form-check form-check-inline" key={item.id}>
                                        <input
                                            type="checkbox"
                                            id={`weather-${item.id}`}
                                            value={item.value}
                                            checked={formData.rainPolicy.includes(item.value)}
                                            onChange={(e) => handleCheckbox(e, "rainPolicy")}
                                            className="form-check"
                                        />
                                        <label htmlFor={`weather-${item.id}`}>
                                            {item.icon} {item.label}
                                        </label>
                                    </div>
                                ))}
                            
                                
                                {["開催", "中止", "決行"].map((policy, idx) => (
                                    
                                    <div className="form-check form-check-inline" key={idx}> 
                                        <input
                                            type="radio" id={`policy-${policy}`} value={policy}
                                            name="policy" className="form-check"
                                            checked={formData.policy.includes(policy)} 
                                            onChange={(e) => handleRadioChange(e, "policy")}
                                        />
                                        <label htmlFor={`policy-${policy}`}>{policy}</label>
                                    </div>
                                ))}
                            </div>    
                        </div>
                    </div>
                )}

                {/* 7.締切 */}
                <div className="form-row-group">
                  <label className="form-label-wide required">締切</label>
                  <DatePartsInput
                    name="regDeadline"
                    value={formData.regDeadline}
                    onChange={(v) => {
                        setFormData(prev => {
                            const next = { ...prev, regDeadline: v };
                            const maxSend = v ? addDays(v, -1) : "";
                            if (next.sendDeadline && maxSend && next.sendDeadline > maxSend) {
                                next.sendDeadline = maxSend;
                            }
                            return next;
                        });
                        if (isVerified) setIsVerified(false);
                    }}
                    min={isEditMode ? undefined : todayYmd()}
                    max={isEditMode ? undefined : eventDayForDeadline}
                    required
                  />
                </div>

                <div className="form-row-group">
                    <label className="form-label-wide required">回答締切</label>
                    <DatePartsInput
                        name="sendDeadline"
                        value={formData.sendDeadline}
                        onChange={(v) => {
                            setFormData(prev => ({ ...prev, sendDeadline: v }));
                            if (isVerified) setIsVerified(false);
                        }}
                        min={isEditMode ? undefined : todayYmd()}
                        // 開催日まで。かつ既存ルールの「締切の前日まで」も満たす必要があるため、早い方を上限にする
                        max={
                            isEditMode
                                ? undefined
                                : earliest(
                                    eventDayForDeadline,
                                    formData.regDeadline ? addDays(formData.regDeadline, -1) : ""
                                )
                        }
                        required
                    />
                </div>

                {/* 8. 作成者  */}
                <div className="form-row-group">
                    <label className="form-label-wide">作成者</label>
                    <div className="form-field-wrapper">
                        <input
                            type="text"
                            value={currentUser.username || "ログイン中ユーザー"}
                            disabled
                            className="input-text-long input-disabled"
                        />
                    </div>
                </div>

                {/* アンケート */}
                <div
                    onDoubleClick={handleDoubleClick}
                    className="anketo"
                    style={{ cursor: 'pointer', marginBottom: '15px', textAlign: 'center',  userSelect: 'none', padding: '10px', backgroundColor: '#f0f0f0',}}
                >
                    <label>
                        <input 
                        type="checkbox"
                        checked={formData.hasAnketo}
                        onChange={(e) => setFormData({...formData, hasAnketo: e.target.checked})}
                        />
                        アンケート
                    </label>
                    <span style={{ marginLeft: '10px', fontSize: '0.9em', color: 'gray' }}>
                        (ダブルクリックで表示/非表示切替)
                    </span>
                </div>
                {/*{showAnketo && (*/}
                {formData.hasAnketo && (
                    <div className="anketo-section">
                    {/*}
                    <p className="anketo-section-intro">
                        各設問は<strong>1問につき1枠</strong>です。設問の直下に、<strong>選択肢を1行につき1つ</strong>入力してください（左の〇は回答形式のイメージです）。
                    </p>
                    
                    {extraQuestions.map((q, index) => {
                        const isObj = q && typeof q === "object" && !Array.isArray(q);
                        const questionText = isObj ? (q.question || "") : String(q || "");
                        const options = isObj && Array.isArray(q.options) ? q.options : [];
                        const optionRows = getOptionRows(options);
                        return (
                            <div key={index} className="anketo-admin-box">
                                <div className="anketo-admin-title">
                                    <span className="anketo-admin-qno" aria-hidden>{`Q${index + 1}`}</span>
                                    <span>設問</span>
                                </div>
                                <div className="event-discription-input">
                                    <textarea
                                        className="input-text-long"
                                        value={questionText}
                                        onChange={(e) => handleExtraQuestionChange(index, { question: e.target.value })}
                                        placeholder="問題を入力してください。"
                                    />
                                </div>

                                <div className="anketo-admin-title anketo-admin-title--options" style={{ marginTop: 14 }}>
                                    <span className="anketo-admin-qno anketo-admin-qno--bullet" aria-hidden>〇</span>
                                    <span>選択肢</span>
                                </div>
                                <div className="anketo-admin-option-rows" role="group" aria-label="選択肢（1行1つ）">
                                    {optionRows.map((opt, oi) => (
                                        <div key={`${index}-opt-${oi}`} className="anketo-admin-option-row">
                                            <span className="anketo-admin-option-bullet" aria-hidden>〇</span>
                                            <input
                                                type="text"
                                                className="anketo-admin-option-input"
                                                value={opt}
                                                onChange={(e) => setOptionRowValue(index, oi, e.target.value)}
                                                placeholder="選択肢を入力"
                                                aria-label={`選択肢 ${oi + 1}`}
                                            />
                                            <button
                                                type="button"
                                                className="anketo-admin-option-remove"
                                                onClick={() => removeOptionRow(index, oi)}
                                                disabled={optionRows.length <= 1}
                                                title={optionRows.length <= 1 ? "最低1行は必要です" : "この行を削除"}
                                            >
                                                削除
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <button type="button" className="anketo-admin-add-option" onClick={() => addOptionRow(index)}>
                                    ＋ 選択肢を追加
                                </button>

                                <div className="anketo-admin-actions">
                                    <button type="button" className="anketo-btn" onClick={() => addQuestionArt(index)}>
                                        追加
                                    </button>
                                    <button
                                        type="button"
                                        className="anketo-btn anketo-btn-danger"
                                        onClick={() => removeQuestionArt(index)}
                                        disabled={index < 5}
                                        title={index < 5 ? "固定5問は削除できません" : "削除"}
                                    >
                                        削除
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                        */}
                    {extraQuestions.map((q, index) => {
                        const isObj = q && typeof q === "object" && !Array.isArray(q);
                        const questionText = isObj ? (q.question || "") : String(q || "");
                        const options = isObj && Array.isArray(q.options) ? q.options : [];
                        const optionRows = getOptionRows(options);

                        return (
                            <div key={index} className="anketo-admin-box">
                                <div className="anketo-combined-orange-box">
                                    
                                    <div className="anketo-question-row">
                                        <span className="anketo-q-label">{`Q${index + 1} : `}</span>
                                        <textarea
                                            className="anketo-question-textarea"
                                            value={questionText}
                                            onChange={(e) => handleExtraQuestionChange(index, { question: e.target.value })}
                                            placeholder="問題を入力してください。"
                                        />
                                    </div>

                                    
                                    <div className="anketo-options-inside">
                                        <div className="anketo-admin-option-rows">
                                            {optionRows.map((opt, oi) => (
                                                <div key={`${index}-opt-${oi}`} className="anketo-admin-option-row">
                                                    <span className="anketo-admin-option-bullet">〇</span>
                                                    <input
                                                        type="text"
                                                        className="anketo-admin-option-input"
                                                        value={opt}
                                                        onChange={(e) => setOptionRowValue(index, oi, e.target.value)}
                                                        placeholder="選択肢を入力"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="anketo-admin-option-remove"
                                                        onClick={() => removeOptionRow(index, oi)}
                                                        disabled={optionRows.length <= 1}
                                                    >
                                                        削除
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        <button type="button" className="anketo-admin-add-option-btn" onClick={() => addOptionRow(index)}>
                                            ＋ 選択肢を追加
                                        </button>
                                    </div>
                                </div>

                                
                                <div className="anketo-admin-actions">
                                    <button type="button" className="anketo-btn" onClick={() => addQuestionArt(index)}>
                                        追加
                                    </button>
                                    <button
                                        type="button"
                                        className="anketo-btn anketo-btn-danger"
                                        onClick={() => removeQuestionArt(index)}
                                        
                                        disabled={index === 0} 
                                        title={index === 0 ? "Q1は削除できません" : "削除"}
                                    >
                                        削除
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                    
                </div>
                )}

                {/* ボタン  */}
                <div className="btn-group-notice event-create-btn-group">
                {/*<button type="button" onClick={handlePreview} className="btn-preview">確認</button>*/}   
                    {/*<button type="button" className="btn-confirm" onClick={handleOpenConfirm}>確認</button>*/}
                    <button type="submit" className={`btn-save ${!isFormValid() ? 'disabled' : ''}`} disabled={!isFormValid()}>保存</button>
                    <button type="button" onClick={handleTempSave} className="btn-save">一時保存</button>
                    <button type="button" onClick={handleCancel} className="btn-back adm-btn">キャンセル</button>
                </div>


                {message && (
                    <p className="success-message">{message}</p>
                )}
            </form>
            {/* 確認画面 */}
            
            {showConfirmModal && (
            <div className="modal-overlay-custom">
                <div className="modal-content-custom commercial-style">
                
                
                <div className="modal-header">
                    <h2 className="modal-title-text">イベント内容の確認</h2>
                    <p className="modal-subtitle-text">以下の内容でイベントを作成しますか？</p>
                </div>

                
                <div className="modal-body-scroll">
                    
                    
                    <div className="confirm-section">
                    <h3 className="section-title">基本情報</h3>
                    <div className="confirm-item">
                        <span className="item-label">イベント名</span>
                        <span className="item-value highlight">{formData.eventName}</span>
                    </div>
                        <div className="confirm-item">
                        <span className="item-label">詳細</span>
                        <span className="item-value">{formData.description}</span>
                        </div>
                    </div>

                    <div className="confirm-section">
                    <h3 className="section-title">日時・期限</h3>
                    <div className="confirm-item">
                        <span className="item-label">開催日</span>
                        <span className="item-value">{dateType === "range" ? `${startDate} ～ ${endDate}` : eventDate}</span>
                    </div>
                    <div className="confirm-item">
                        <span className="item-label">時間</span>
                        <span className="item-value">{formData.startTime}～{formData.endTime}</span>
                    </div>
                    <div className="confirm-item">
                        <span className="item-label">締切日</span>
                        <span className="item-value deadline">{formData.regDeadline}</span>
                    </div>
                    <div className="confirm-item">
                        <span className="item-label">回答締切</span>
                        <span className="item-value deadline">{formData.sendDeadline}</span>
                    </div>
                    </div>

                    {/* 対象者 */}
                    <div className="confirm-section">
                        <h3 className="section-title">対象者</h3>
                        <ul className="confirm-target-group-list">
                            {formData.targetGroup.map((t) => (
                                <li key={t} className="target-group-item">{t}</li>
                            ))}
                        </ul>
                    </div>
                    {/* 天候 */}
                    <div className="confirm-section">
                        <h3 className="section-title">天候</h3>
                        <ul className="confirm-rain-policy-list">
                            {formData.rainPolicy.map((p) => (
                                <li key={p} className="rain-policy-item">{p}</li>
                            ))}
                        </ul>
                    </div>
                    {/* 開催条件 */}
                    <div className="confirm-section">
                        <h3 className="section-title">開催条件</h3>
                        <ul className="confirm-policy-list">
                            {formData.policy.map((p) => (
                                <li key={p} className="policy-item">{p}</li>
                            ))}
                        </ul>
                    </div>

                    {/* 開催場所 */}
                    <div className="confirm-section">
                        <h3 className="section-title">開催場所</h3>
                        <span className="item-value">{formData.location}</span>
                    </div>

                    {/* 作成者 */}
                    <div className="confirm-section">
                        <h3 className="section-title">作成者</h3>
                        <span className="item-value">{currentUser.username || "ログイン中ユーザー"}</span>
                    </div>
                    
                    {/* アンケート */}
                    {extraQuestions.some((q) => (typeof q === "string" ? q.trim() : String(q?.question || "").trim())) && (
                    <div className="confirm-section">
                        <h3 className="section-title">アンケート項目</h3>
                        <ul className="confirm-anketo-list">
                        {
                            extraQuestions
                            .filter((q) => (typeof q === "string" ? q.trim() : String(q?.question || "").trim()))
                            .map((q, index) => (
                            <li key={index} className="anketo-item">
                                <span className="q-number">Q{index + 1}.</span>{" "}
                                {typeof q === "string" ? q : q.question}
                            </li>
                        ))}
                        </ul>
                    </div>
                    )}
                   
                </div>

                
                <div className="modal-btns">
                    <button className="btn-modal-edit" onClick={() => setShowConfirmModal(false)}>修正</button>
                    <button className="btn-modal-confirm" onClick={() => {
                        verifiedSignatureRef.current = getConfirmSignature();
                        setIsVerified(true);
                        setShowConfirmModal(false);
                    }}>確認しました</button>
                </div>
                </div>
            </div>
            )}
        </div>
    );
};

export default EventCreate;
